<script setup>
// Nó customizado do Vue Flow — espelha os node cards do projeto original:
// card colorido, ícone + nome, prévia da config, handles topo/baixo, botão excluir no hover.
// Condição e switch expõem múltiplas saídas (handles inferiores rotulados).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Handle, Position } from '@vue-flow/core';
import { nodeMeta, nodeColor } from './flowNodeTypes';

const props = defineProps({
  id: { type: String, required: true },
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();

const type = computed(() => props.data.nodeType);
const meta = computed(() => nodeMeta(type.value));
const color = computed(() => nodeColor(type.value));
const name = computed(() => props.data.title || t(meta.value.label));
const previewText = computed(() => {
  try {
    return meta.value.preview(props.data, t) || t(meta.value.desc);
  } catch (e) {
    return t(meta.value.desc);
  }
});

const isStart = computed(() => type.value === 'start');
const isEnd = computed(() => type.value === 'end');
// Miniatura/prévia da mídia enviada (imagem/vídeo/áudio) no próprio nó após o upload.
const mediaThumb = computed(() => {
  const d = props.data || {};
  if (type.value === 'sendImage' && d.imageUrl) return { kind: 'image', url: d.imageUrl };
  if (type.value === 'sendVideo' && d.videoUrl) return { kind: 'video', url: d.videoUrl };
  if (type.value === 'sendAudio' && d.audioUrl) return { kind: 'audio', url: d.audioUrl };
  return null;
});
// O Agente de IA expõe um handle extra "tools" (lado direito): nós conectados
// nele viram ferramentas (function calling) que o modelo pode chamar.
const hasToolsHandle = computed(() => type.value === 'aiAgent');

// saídas (handles inferiores)
const outputs = computed(() => {
  if (type.value === 'condition')
    return [
      { id: 'true', label: t('CHATBOT.nodes.condition.yes'), color: '#16a34a' },
      { id: 'false', label: t('CHATBOT.nodes.condition.no'), color: '#dc2626' },
    ];
  if (type.value === 'switch') {
    const cases = (props.data.cases || []).map((c, i) => ({
      id: `case-${i}`,
      label: c.label || t('CHATBOT.nodeConfig.switch.caseTitle', { n: i + 1 }),
      color: '#4f46e5',
    }));
    if (props.data.fallback)
      cases.push({
        id: 'fallback',
        label: t('CHATBOT.nodes.switch.fallback'),
        color: '#64748b',
      });
    return cases.length
      ? cases
      : [{ id: 'source', label: '', color: color.value.accent }];
  }
  return [{ id: 'source', label: '', color: color.value.accent }];
});

const onDelete = e => {
  e.stopPropagation();
  if (props.data.onDelete) props.data.onDelete(props.id);
};
</script>

<template>
  <div
    class="relative min-w-[200px] max-w-[250px] p-3 rounded-lg border-2 group"
    :style="{
      background: color.bg,
      borderColor: selected || data.isCurrent ? color.accent : color.border,
      boxShadow: data.isCurrent
        ? '0 0 0 3px rgba(37,99,235,0.45)'
        : selected
          ? '0 6px 16px rgba(0,0,0,0.12)'
          : 'none',
    }"
  >
    <span
      v-if="data.isCurrent"
      class="absolute -top-2.5 left-2 px-1.5 py-0.5 rounded-full text-[0.625rem] font-medium text-white bg-n-brand inline-flex items-center gap-0.5"
      ><svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="w-3 h-3"
      >
        <path d="M12 21s-6-5.686-6-10a6 6 0 0 1 12 0c0 4.314-6 10-6 10z" />
        <circle cx="12" cy="11" r="2" />
      </svg>
      {{ t('CHATBOT.journey.here') }}</span
    >
    <Handle
      v-if="!isStart"
      type="target"
      :position="Position.Top"
      :style="{ background: color.accent }"
    />

    <button
      class="absolute -top-[7px] -right-[7px] w-[16px] h-[16px] flex items-center justify-center p-0 rounded-full bg-n-ruby-9 text-white border-[1.5px] border-white shadow opacity-0 transition group-hover:opacity-100 hover:scale-110 cursor-pointer"
      :title="t('CHATBOT.nodes.common.delete')"
      @click="onDelete"
    >
      <svg
        class="w-[10px] h-[10px]"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
      >
        <path stroke-linecap="round" d="M4 4l8 8M12 4l-8 8" />
      </svg>
    </button>

    <div class="flex items-center gap-2 mb-1.5">
      <svg
        class="w-5 h-5 shrink-0"
        :style="{ color: color.accent }"
        viewBox="0 0 24 24"
        :fill="meta.fill ? 'currentColor' : 'none'"
        :stroke="meta.fill ? 'none' : 'currentColor'"
        stroke-width="2"
      >
        <path
          v-for="(d, i) in meta.icon"
          :key="i"
          stroke-linecap="round"
          stroke-linejoin="round"
          :d="d"
        />
      </svg>
      <span
        class="text-sm font-semibold truncate"
        :style="{ color: color.accent }"
        >{{ name }}</span
      >
    </div>
    <p class="text-xs truncate text-n-slate-11" :title="previewText">
      {{ previewText }}
    </p>

    <!-- miniatura da mídia enviada -->
    <div
      v-if="mediaThumb"
      class="mt-2 overflow-hidden border rounded-md border-n-weak bg-n-alpha-1"
    >
      <img
        v-if="mediaThumb.kind === 'image'"
        :src="mediaThumb.url"
        class="object-cover w-full h-20"
      />
      <video
        v-else-if="mediaThumb.kind === 'video'"
        :src="mediaThumb.url"
        class="object-cover w-full h-20"
        muted
        preload="metadata"
      />
      <audio
        v-else
        :src="mediaThumb.url"
        controls
        class="w-full h-8"
        @mousedown.stop
        @click.stop
      />
    </div>

    <!-- saídas -->
    <template v-if="!isEnd">
      <Handle
        v-for="(o, idx) in outputs"
        :id="o.id"
        :key="o.id"
        type="source"
        :position="Position.Bottom"
        :style="{
          background: o.color,
          left:
            outputs.length > 1
              ? `${((idx + 1) * 100) / (outputs.length + 1)}%`
              : hasToolsHandle
                ? '35%'
                : '50%',
        }"
      />
      <div
        v-if="outputs.length > 1"
        class="flex justify-around px-1 mt-1 -mb-1"
      >
        <span
          v-for="o in outputs"
          :key="o.id"
          class="text-[0.5625rem] font-medium"
          :style="{ color: o.color }"
          >{{ o.label }}</span
        >
      </div>
    </template>

    <!-- handle de ferramentas (parte de baixo, ao lado da saída principal) do Agente de IA -->
    <template v-if="hasToolsHandle">
      <Handle
        id="tools"
        type="source"
        :position="Position.Bottom"
        :style="{
          background: '#a855f7',
          left: '65%',
          width: '10px',
          height: '10px',
        }"
      />
      <div class="flex px-1 mt-1 -mb-1 text-[0.5625rem] font-medium">
        <span class="text-n-slate-10" style="margin-left: 26%">{{
          t('CHATBOT.nodes.aiAgent.flowHandle')
        }}</span>
        <span class="text-n-violet-11" style="margin-left: auto; margin-right: 22%">{{
          t('CHATBOT.nodes.aiAgent.toolsHandle')
        }}</span>
      </div>
    </template>
  </div>
</template>
